import * as THREE from 'three';
import { Track, SampledSpline, MAIN_SPLINE, makeFrame } from '@voidline/shared';
import { deckTexture, boostTexture, hazardTexture, finishTexture } from './proctex.js';

export interface TrackMeshOptions {
  edgeColor: THREE.ColorRepresentation;
  edgeColorAlt: THREE.ColorRepresentation;
  wallColor: THREE.ColorRepresentation;
  colorAssist: boolean;
}

export interface TrackMeshHandle {
  group: THREE.Group;
  update(dt: number, time: number): void;
  dispose(): void;
}

/** Build all render geometry for a track: deck, edge strips, walls, pads, lines. */
export function buildTrackMeshes(track: Track, opts: TrackMeshOptions): TrackMeshHandle {
  const group = new THREE.Group();
  const disposables: { dispose(): void }[] = [];

  const deckTex = deckTexture();
  disposables.push(deckTex);
  const deckMat = new THREE.MeshStandardMaterial({
    map: deckTex,
    roughness: 0.82,
    metalness: 0.35,
    side: THREE.DoubleSide,
  });
  disposables.push(deckMat);

  const edgeMat = new THREE.MeshBasicMaterial({ color: opts.edgeColor, toneMapped: false });
  const edgeMatAlt = new THREE.MeshBasicMaterial({ color: opts.edgeColorAlt, toneMapped: false });
  const edgeWarnMat = new THREE.MeshBasicMaterial({ color: 0xff4433, toneMapped: false });
  disposables.push(edgeMat, edgeMatAlt, edgeWarnMat);

  // kept faint: additive walls stack up at grazing angles and can wash out the
  // whole road ahead if this is much higher
  const wallMat = new THREE.MeshBasicMaterial({
    color: opts.wallColor,
    transparent: true,
    opacity: 0.05,
    side: THREE.DoubleSide,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  disposables.push(wallMat);

  // main + branch ribbons
  buildRibbon(track, track.main, MAIN_SPLINE, group, deckMat, edgeMat, edgeMatAlt, edgeWarnMat, wallMat, disposables, opts);
  track.branches.forEach((b, i) => {
    buildRibbon(track, b.spline, i + 1, group, deckMat, edgeMat, edgeMatAlt, edgeWarnMat, wallMat, disposables, opts);
  });

  // ── boost pads ──
  const boostTex = boostTexture();
  disposables.push(boostTex);
  const boostMat = new THREE.MeshBasicMaterial({
    map: boostTex,
    transparent: true,
    opacity: 0.95,
    toneMapped: false,
    depthWrite: false,
  });
  disposables.push(boostMat);
  const frame = makeFrame();
  for (const pad of track.boostPads) {
    const spline = track.spline(pad.splineId);
    const len = pad.sEnd - pad.sStart;
    const segs = Math.max(2, Math.ceil(len / 4));
    const geo = new THREE.PlaneGeometry(pad.halfWidth * 2, len, 1, segs);
    const posAttr = geo.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < posAttr.count; i++) {
      const localX = posAttr.getX(i);
      const localY = posAttr.getY(i);
      const s = pad.sStart + (localY / len + 0.5) * len;
      spline.frameAt(s, frame);
      const lateral = (pad.lane * frame.width) / 2 + localX;
      posAttr.setXYZ(
        i,
        frame.pos.x + frame.right.x * lateral + frame.up.x * 0.09,
        frame.pos.y + frame.right.y * lateral + frame.up.y * 0.09,
        frame.pos.z + frame.right.z * lateral + frame.up.z * 0.09
      );
    }
    geo.computeVertexNormals();
    const mesh = new THREE.Mesh(geo, boostMat);
    mesh.frustumCulled = true;
    group.add(mesh);
    disposables.push(geo);
  }

  // ── start/finish line ──
  {
    const finTex = finishTexture();
    disposables.push(finTex);
    const finMat = new THREE.MeshBasicMaterial({ map: finTex, toneMapped: false });
    disposables.push(finMat);
    track.main.frameAt(track.main.closed ? 0 : 2, frame);
    const geo = new THREE.PlaneGeometry(frame.width, 4);
    transformCrossSection(geo, track.main, track.main.closed ? 0 : 2, 0.12);
    group.add(new THREE.Mesh(geo, finMat));
    disposables.push(geo);
  }

  // ── checkpoint lines (skip index 0 = finish) ──
  {
    const cpMat = new THREE.MeshBasicMaterial({
      color: opts.colorAssist ? 0xffffff : 0x31c8ff,
      transparent: true,
      opacity: 0.4,
      toneMapped: false,
      depthWrite: false,
    });
    disposables.push(cpMat);
    for (let i = 1; i < track.checkpoints.length; i++) {
      const s = track.checkpoints[i];
      track.main.frameAt(s, frame);
      const geo = new THREE.PlaneGeometry(frame.width, 0.8);
      transformCrossSection(geo, track.main, s, 0.1);
      group.add(new THREE.Mesh(geo, cpMat));
      disposables.push(geo);
    }
  }

  // ── hazards ──
  const hazardTex = hazardTexture();
  disposables.push(hazardTex);
  for (const hz of track.hazards) {
    if (hz.type === 'barrier') {
      const mat = new THREE.MeshStandardMaterial({
        map: hazardTex,
        roughness: 0.6,
        metalness: 0.3,
        emissive: 0xaa6600,
        emissiveIntensity: 0.25,
      });
      disposables.push(mat);
      const len = hz.sEnd - hz.sStart;
      const mid = (hz.sStart + hz.sEnd) / 2;
      track.main.frameAt(mid, frame);
      const geo = new THREE.BoxGeometry(hz.halfWidth * 2, 2.4, len);
      const mesh = new THREE.Mesh(geo, mat);
      const lateral = (hz.lane * frame.width) / 2;
      mesh.position.set(
        frame.pos.x + frame.right.x * lateral + frame.up.x * 1.2,
        frame.pos.y + frame.right.y * lateral + frame.up.y * 1.2,
        frame.pos.z + frame.right.z * lateral + frame.up.z * 1.2
      );
      orientToFrame(mesh, frame);
      group.add(mesh);
      disposables.push(geo);
    } else {
      // slick: dark glossy patch
      const mat = new THREE.MeshStandardMaterial({
        color: 0x0a1418,
        roughness: 0.08,
        metalness: 0.9,
        transparent: true,
        opacity: 0.85,
      });
      disposables.push(mat);
      const len = hz.sEnd - hz.sStart;
      const geo = new THREE.PlaneGeometry(hz.halfWidth * 2, len, 1, Math.max(2, Math.ceil(len / 6)));
      const posAttr = geo.attributes.position as THREE.BufferAttribute;
      for (let i = 0; i < posAttr.count; i++) {
        const localX = posAttr.getX(i);
        const s = hz.sStart + (posAttr.getY(i) / len + 0.5) * len;
        track.main.frameAt(s, frame);
        const lateral = (hz.lane * frame.width) / 2 + localX;
        posAttr.setXYZ(
          i,
          frame.pos.x + frame.right.x * lateral + frame.up.x * 0.06,
          frame.pos.y + frame.right.y * lateral + frame.up.y * 0.06,
          frame.pos.z + frame.right.z * lateral + frame.up.z * 0.06
        );
      }
      geo.computeVertexNormals();
      group.add(new THREE.Mesh(geo, mat));
      disposables.push(geo);
    }
  }

  let pulse = 0;
  return {
    group,
    update(dt: number, _time: number) {
      pulse += dt;
      boostTex.offset.y = -pulse * 1.6;
      boostTex.needsUpdate = false; // offset doesn't need re-upload
      boostMat.opacity = 0.8 + Math.sin(pulse * 6) * 0.15;
      wallMat.opacity = 0.045 + Math.sin(pulse * 2.2) * 0.012;
    },
    dispose() {
      group.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}

function buildRibbon(
  track: Track,
  spline: SampledSpline,
  splineId: number,
  group: THREE.Group,
  deckMat: THREE.Material,
  edgeMat: THREE.Material,
  edgeMatAlt: THREE.Material,
  edgeWarnMat: THREE.Material,
  wallMat: THREE.Material,
  disposables: { dispose(): void }[],
  _opts: TrackMeshOptions
): void {
  const n = spline.count;
  const closed = spline.closed;
  const segCount = closed ? n : n - 1;

  const inJump = (s: number) => splineId === MAIN_SPLINE && track.isInJumpZone(splineId, s);

  // ── deck ──
  {
    const positions: number[] = [];
    const normals: number[] = [];
    const uvs: number[] = [];
    const indices: number[] = [];
    const frame = makeFrame();
    // vertices for every sample (even in jump zones — we skip triangles there)
    for (let i = 0; i < n; i++) {
      const s = i * spline.spacing;
      spline.frameAt(s, frame);
      const hw = frame.width / 2;
      // left, right + skirt verts (angled down-out for a chunky silhouette)
      const L = frame.pos.clone().addScaled(frame.right, -hw);
      const R = frame.pos.clone().addScaled(frame.right, hw);
      const LS = L.clone().addScaled(frame.right, -1.4).addScaled(frame.up, -2.2);
      const RS = R.clone().addScaled(frame.right, 1.4).addScaled(frame.up, -2.2);
      positions.push(LS.x, LS.y, LS.z, L.x, L.y, L.z, R.x, R.y, R.z, RS.x, RS.y, RS.z);
      for (let k = 0; k < 4; k++) normals.push(frame.up.x, frame.up.y, frame.up.z);
      const v = s / 12;
      uvs.push(-0.1, v, 0, v, frame.width / 12, v, frame.width / 12 + 0.1, v);
    }
    for (let i = 0; i < segCount; i++) {
      const s = i * spline.spacing;
      if (inJump(s)) continue;
      const a = i * 4;
      const b = ((i + 1) % n) * 4;
      // skirt L, deck, skirt R
      for (let q = 0; q < 3; q++) {
        indices.push(a + q, b + q, a + q + 1, a + q + 1, b + q, b + q + 1);
      }
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    geo.setIndex(indices);
    const mesh = new THREE.Mesh(geo, deckMat);
    mesh.receiveShadow = false;
    group.add(mesh);
    disposables.push(geo);
  }

  // ── edge strips + walls ──
  {
    const stripL: number[] = [];
    const stripR: number[] = [];
    const stripWarnL: number[] = [];
    const stripWarnR: number[] = [];
    const wallVerts: number[] = [];
    const frame = makeFrame();
    const pushStrip = (arr: number[], i0: number, i1: number, side: number) => {
      // quad between samples i0,i1 on the given side (side=-1 left, +1 right)
      for (const ii of [i0, i1]) {
        const s = ii * spline.spacing;
        spline.frameAt(s, frame);
        const hw = frame.width / 2;
        const outer = frame.pos.clone().addScaled(frame.right, side * hw).addScaled(frame.up, 0.15);
        const inner = frame.pos
          .clone()
          .addScaled(frame.right, side * (hw - 0.65))
          .addScaled(frame.up, 0.15);
        arr.push(outer.x, outer.y, outer.z, inner.x, inner.y, inner.z);
      }
    };
    const pushWall = (i0: number, i1: number, side: number) => {
      for (const ii of [i0, i1]) {
        const s = ii * spline.spacing;
        spline.frameAt(s, frame);
        const hw = frame.width / 2;
        const base = frame.pos.clone().addScaled(frame.right, side * hw);
        const top = base.clone().addScaled(frame.up, 2.6);
        wallVerts.push(base.x, base.y, base.z, top.x, top.y, top.z);
      }
    };

    for (let i = 0; i < segCount; i++) {
      const s = i * spline.spacing;
      if (inJump(s)) continue;
      const j = (i + 1) % n;
      const walls =
        splineId === MAIN_SPLINE ? track.wallsAt(splineId, s) : { left: true, right: true };
      pushStrip(walls.left ? stripL : stripWarnL, i, j, -1);
      pushStrip(walls.right ? stripR : stripWarnR, i, j, 1);
      if (walls.left) pushWall(i, j, -1);
      if (walls.right) pushWall(i, j, 1);
    }

    const buildQuads = (verts: number[], mat: THREE.Material) => {
      if (verts.length === 0) return;
      const quadCount = verts.length / 12;
      const indices: number[] = [];
      for (let q = 0; q < quadCount; q++) {
        const b = q * 4;
        indices.push(b, b + 2, b + 1, b + 1, b + 2, b + 3);
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
      geo.setIndex(indices);
      const mesh = new THREE.Mesh(geo, mat);
      group.add(mesh);
      disposables.push(geo);
    };
    buildQuads(stripL, edgeMat);
    buildQuads(stripR, edgeMatAlt);
    buildQuads(stripWarnL, edgeWarnMat);
    buildQuads(stripWarnR, edgeWarnMat);
    buildQuads(wallVerts, wallMat);
  }
}

/** Position a PlaneGeometry (built in XY) across the track at arc position s. */
function transformCrossSection(geo: THREE.PlaneGeometry, spline: SampledSpline, s: number, lift: number): void {
  const frame = makeFrame();
  spline.frameAt(s, frame);
  const posAttr = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < posAttr.count; i++) {
    const x = posAttr.getX(i);
    const y = posAttr.getY(i);
    posAttr.setXYZ(
      i,
      frame.pos.x + frame.right.x * x + frame.tangent.x * y + frame.up.x * lift,
      frame.pos.y + frame.right.y * x + frame.tangent.y * y + frame.up.y * lift,
      frame.pos.z + frame.right.z * x + frame.tangent.z * y + frame.up.z * lift
    );
  }
  geo.computeVertexNormals();
}

function orientToFrame(obj: THREE.Object3D, frame: { tangent: any; up: any; right: any }): void {
  const m = new THREE.Matrix4().makeBasis(
    new THREE.Vector3(frame.right.x, frame.right.y, frame.right.z),
    new THREE.Vector3(frame.up.x, frame.up.y, frame.up.z),
    new THREE.Vector3(frame.tangent.x, frame.tangent.y, frame.tangent.z)
  );
  obj.quaternion.setFromRotationMatrix(m);
}
